import React, { Dispatch, useEffect, useState } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  JobDescription,
  showAllTypes,
} from '../../../../../../utils/types/JobOpenings';
import AddRemoveComponent from '../../../ui/AddOrRemoveList';
import Collapsible from '../../../ui/CollapsibleChildren';

interface CollapsibleData {
  id: number;
  title: string;
  labels: string[];
  isOpen: boolean;
  droppableId: string;
}

type ListFieldsProps = {
  showAll: showAllTypes;
  setShowAll: React.Dispatch<React.SetStateAction<showAllTypes>>;
  jobDescription: JobDescription;
  setJobDescription: Dispatch<React.SetStateAction<JobDescription>>;
  disabled?: boolean;
  editable?: boolean;
};

const ListFields = ({
  showAll,
  setShowAll,
  jobDescription,
  setJobDescription,
  disabled = false,
  editable = true,
}: ListFieldsProps) => {
  const { t } = useTranslation(NS.adminPages);
  const JD = ADMIN_PAGES_KEYS.jobOpenings.jobDescription;

  const [collapsible, setCollapsible] = useState<CollapsibleData[]>([
    {
      id: 1,
      title: t(JD.responsibilitiesTitle),
      labels: jobDescription.responsibilities || [],
      isOpen: true,
      droppableId: 'responsibilities',
    },
    {
      id: 2,
      title: t(JD.educationsTitle),
      labels: jobDescription.educations || [],
      isOpen: true,
      droppableId: 'educations',
    },
    {
      id: 3,
      title: t(JD.certificationsTitle),
      labels: jobDescription.certifications || [],
      isOpen: true,
      droppableId: 'certifications',
    },
    {
      id: 4,
      title: t(JD.mandatorySkillsTitle),
      labels: jobDescription.mandatorySkills || [],
      isOpen: true,
      droppableId: 'mandatorySkills',
    },
    {
      id: 5,
      title: t(JD.desirableSkillsTitle),
      labels: jobDescription.desirableSkills || [],
      isOpen: true,
      droppableId: 'desirableSkills',
    },
    {
      id: 6,
      title: t(JD.behavioralAttributesTitle),
      labels: jobDescription.behavioralAttributes || [],
      isOpen: true,
      droppableId: 'behavioralAttributes',
    },
  ]);

  useEffect(() => {
    setCollapsible((prevCollapsible) => [
      {
        ...prevCollapsible[0],
        labels: jobDescription.responsibilities || [],
      },
      {
        ...prevCollapsible[1],
        labels: jobDescription.educations || [],
      },
      {
        ...prevCollapsible[2],
        labels: jobDescription.certifications || [],
      },
      {
        ...prevCollapsible[3],
        labels: jobDescription.mandatorySkills || [],
      },
      {
        ...prevCollapsible[4],
        labels: jobDescription.desirableSkills || [],
      },
      {
        ...prevCollapsible[5],
        labels: jobDescription.behavioralAttributes || [],
      },
    ]);
  }, [
    jobDescription.responsibilities,
    jobDescription.educations,
    jobDescription.certifications,
    jobDescription.mandatorySkills,
    jobDescription.desirableSkills,
    jobDescription.behavioralAttributes,
  ]);

  useEffect(() => {
    let newIsOpenState: boolean;
    if (showAll === 'showAll') {
      newIsOpenState = true;
    } else if (showAll === 'collapseAll') {
      newIsOpenState = false;
    } else {
      return;
    }

    setCollapsible(
      collapsible.map((collapsibleItem) => ({
        ...collapsibleItem,
        isOpen: newIsOpenState,
      })),
    );
  }, [showAll]);

  useEffect(() => {
    const hasChanges =
      JSON.stringify(collapsible[0].labels) !==
        JSON.stringify(jobDescription.responsibilities) ||
      JSON.stringify(collapsible[1].labels) !==
        JSON.stringify(jobDescription.educations) ||
      JSON.stringify(collapsible[2].labels) !==
        JSON.stringify(jobDescription.certifications) ||
      JSON.stringify(collapsible[3].labels) !==
        JSON.stringify(jobDescription.mandatorySkills) ||
      JSON.stringify(collapsible[4].labels) !==
        JSON.stringify(jobDescription.desirableSkills) ||
      JSON.stringify(collapsible[5].labels) !==
        JSON.stringify(jobDescription.behavioralAttributes);

    if (hasChanges) {
      setJobDescription({
        ...jobDescription,
        responsibilities: collapsible[0].labels,
        educations: collapsible[1].labels,
        certifications: collapsible[2].labels,
        mandatorySkills: collapsible[3].labels,
        desirableSkills: collapsible[4].labels,
        behavioralAttributes: collapsible[5].labels,
      });
    }
  }, [collapsible]);

  const toggleOpen = (id: number) => {
    const newCollapsible = collapsible.map((collapsible) =>
      collapsible.id === id
        ? { ...collapsible, isOpen: !collapsible.isOpen }
        : collapsible,
    );
    setCollapsible(newCollapsible);

    const areAllOpen = newCollapsible.every((c) => c.isOpen);
    const areAllClosed = newCollapsible.every((c) => !c.isOpen);

    if (areAllOpen) {
      setShowAll('showAll');
    } else if (areAllClosed) {
      setShowAll('collapseAll');
    } else {
      setShowAll('random');
    }
  };

  const handleLabelsChange = (id: number, updatedLabels: string[]) => {
    setCollapsible((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, labels: updatedLabels } : item,
      ),
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // Dropped outside a list
    if (!destination) return;

    // Dropped in the same position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceSection = collapsible.find(
      (c) => c.droppableId === source.droppableId,
    );
    const destSection = collapsible.find(
      (c) => c.droppableId === destination.droppableId,
    );

    if (!sourceSection || !destSection) return;

    const sourceLabels = Array.from(sourceSection.labels);
    const destLabels = Array.from(destSection.labels);
    const [movedLabel] = sourceLabels.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      // Reordering within the same section
      sourceLabels.splice(destination.index, 0, movedLabel);
      setCollapsible((prev) =>
        prev.map((c) =>
          c.droppableId === source.droppableId
            ? { ...c, labels: sourceLabels }
            : c,
        ),
      );
    } else {
      // Moving between different sections
      destLabels.splice(destination.index, 0, movedLabel);
      setCollapsible((prev) =>
        prev.map((c) => {
          if (c.droppableId === source.droppableId)
            return { ...c, labels: sourceLabels };
          if (c.droppableId === destination.droppableId)
            return { ...c, labels: destLabels };
          return c;
        }),
      );
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-200'>
        { collapsible.map(({ id, title, labels, isOpen, droppableId }) => (
          <div
              key={id}
              className={`transition-opacity duration-300 h-full ${
                isOpen ? 'opacity-100' : 'opacity-90'
              } `}
          >
            <Collapsible
                title={`${title} ${
                  labels.length > 0 ? `(${labels.length})` : ''
                }`}
                isOpen={isOpen}
                toggleOpen={() => toggleOpen(id)}
            >
              <AddRemoveComponent
                  initialLabels={labels}
                  editable={editable}
                  disabled={disabled}
                  droppableId={droppableId}
                  onLabelsChange={(updatedLabels) =>
                    handleLabelsChange(id, updatedLabels)
                  }
                  title={title}
              />
            </Collapsible>
          </div>
        )) }
      </div>
    </DragDropContext>
  );
};

export default ListFields;
